import React, { useState, useEffect } from 'react';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useGameContext } from '../../context/GameContext';
import { fetchGamesForWeek } from '../../adminApi';
import styles from '../../styles/GamesDataBrowser.module.css';

const GamesDataBrowser = () => {
  const { calendar } = useCurrentWeek();
  const { games: cachedGames } = useGameContext();
  const [activeTab, setActiveTab] = useState('clientCache');
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGames = async () => {
      if (!selectedWeek) return;
      
      setLoading(true);
      
      if (activeTab === 'clientCache') {
        const weekGames = cachedGames.filter(game => game.week === parseInt(selectedWeek));
        const uniqueGames = Array.from(new Map(weekGames.map(game => [game.id, game])).values());
        setGames(uniqueGames);
        setLoading(false);
      } else {
        try {
          const response = await fetchGamesForWeek(selectedWeek);
          const uniqueGames = Array.from(new Map(response.data.map(game => [game.id, game])).values());
          setGames(uniqueGames);
          setError(null);
        } catch (err) {
          setError('Failed to fetch games data');
          console.error('Error fetching games:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchGames();
  }, [selectedWeek, activeTab, cachedGames]);

  const handleGameSelect = (game) => {
    setSelectedGame(game);
  };

  const hasGamesForWeek = (weekNumber) => {
    if (activeTab === 'clientCache') {
      return cachedGames.some(game => game.week === parseInt(weekNumber));
    }
    return games.some(game => game.week === parseInt(weekNumber));
  };

  return (
    <div className={styles.gamesDataBrowser}>
      <div className={styles.tabs}>
        <button 
          className={activeTab === 'clientCache' ? styles.activeTab : ''} 
          onClick={() => {
            setActiveTab('clientCache');
            setSelectedGame(null);
          }}
        >
          Client Cache
        </button>
        <button 
          className={activeTab === 'database' ? styles.activeTab : ''} 
          onClick={() => {
            setActiveTab('database');
            setSelectedGame(null);
          }}
        >
          Database
        </button>
      </div>

      <div className={styles.weekSelector}>
        <select 
          value={selectedWeek || ''} 
          onChange={(e) => {
            setSelectedWeek(e.target.value);
            setSelectedGame(null);
          }}
        >
          <option value="">Select a week...</option>
          {calendar?.filter(week => !week.hide_week).map((week) => {
            const weekKey = `${week.season}_${week.week}_${week.season_type}`;
            return (
              <option 
                key={weekKey}
                value={week.week}
                className={hasGamesForWeek(week.week) ? styles.hasGames : ''}
              >
                Week {week.week} ({new Date(week.start_date).toLocaleDateString()} - {new Date(week.end_date).toLocaleDateString()})
              </option>
            );
          })}
        </select>
      </div>

      <div className={styles.browserContent}>
        <div className={styles.gamesList}>
          {loading ? (
            <div className={styles.loading}>Loading games...</div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <ul>
              {games.map(game => (
                <li 
                  key={game.id}
                  className={selectedGame?.id === game.id ? styles.selected : ''}
                  onClick={() => handleGameSelect(game)}
                >
                  {(game.away_team?.name || game.away_team_name)} @ {(game.home_team?.name || game.home_team_name)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.gameDetails}>
          {selectedGame ? (
            <div className={styles.detailsContent}>
              <h3>Game Details</h3>
              <pre>{JSON.stringify(selectedGame, null, 2)}</pre>
            </div>
          ) : (
            <div className={styles.noSelection}>
              Select a game to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamesDataBrowser;
